'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiArrowLeft, FiCheck, FiChevronRight, FiMapPin, FiSearch } from 'react-icons/fi';
import { countryFlagEmoji } from './country-flag';
import { getSubregionName, getSubregionsForCountry } from 'shared/src/region/admin1-registry';
import { fillRegionMapString } from '@/lib/ui-content/region-map-ui-content';
import type { RegionMapUiContent } from '@/lib/ui-content/region-map-ui-content';

/** Above this many entries a plain list stops being scannable. */
const FILTER_THRESHOLD = 20;

function normalize(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();
}

/** A resolved place awaiting confirmation (Places search / detect chip). */
export type SubdivisionConfirmTarget = {
  city: string;
  /** Localized subregion name, when the place resolved to one. */
  areaName?: string | null;
};

export interface SubdivisionPanelProps {
  /** Internal country code (UK, not GB). */
  countryCode: string;
  countryName: string;
  /** Language or full locale, for localized subregion names. */
  lang?: string;
  content: RegionMapUiContent;
  onSelectSubregion: (code: string, name: string) => void;
  onSelectWholeCountry: () => void;
  onBack: () => void;
  /** Subregion rendered as chosen — mirrors the map's `preselect`. */
  selectedCode?: string | null;
  /** Controlled hover shared with the map, in both directions. */
  hoveredCode?: string | null;
  onHoverChange?: (code: string | null) => void;
  confirm?: SubdivisionConfirmTarget | null;
  onConfirm?: () => void;
  /** 'panel' = desktop right column (owns the Back row); 'sheet' = mobile. */
  variant?: 'panel' | 'sheet';
  className?: string;
}

export function SubdivisionPanel({
  countryCode,
  countryName,
  lang,
  content,
  onSelectSubregion,
  onSelectWholeCountry,
  onBack,
  selectedCode = null,
  hoveredCode = null,
  onHoverChange,
  confirm = null,
  onConfirm,
  variant = 'panel',
  className = '',
}: SubdivisionPanelProps) {
  const [query, setQuery] = useState('');
  const listRef = useRef<HTMLUListElement>(null);
  /** True while the pointer is over this list, so map-driven hover can scroll
   *  a row into view without fighting the user's own cursor. */
  const pointerInsideRef = useRef(false);

  // `listOnly` units (French overseas, Ceuta/Melilla, ...) have no geometry but
  // are perfectly valid choices — the list is the only place they can be picked.
  const units = useMemo(() => {
    const rows = getSubregionsForCountry(countryCode).map((unit) => ({
      code: unit.code,
      name: getSubregionName(countryCode, unit.code, lang) ?? unit.name,
      listOnly: unit.listOnly === true,
    }));
    return rows.sort((a, b) => a.name.localeCompare(b.name));
  }, [countryCode, lang]);

  const showFilter = units.length > FILTER_THRESHOLD;

  const visible = useMemo(() => {
    const needle = normalize(query);
    if (!needle) return units;
    return units.filter(
      (unit) => normalize(unit.name).includes(needle) || normalize(unit.code).startsWith(needle),
    );
  }, [units, query]);

  // Hover arriving from the map should reveal the matching row.
  useEffect(() => {
    if (!hoveredCode || pointerInsideRef.current) return;
    const row = listRef.current?.querySelector<HTMLElement>(`[data-subregion="${hoveredCode}"]`);
    row?.scrollIntoView({ block: 'nearest' });
  }, [hoveredCode]);

  const setHover = useCallback((code: string | null) => onHoverChange?.(code), [onHoverChange]);

  const allOfCountryLabel = fillRegionMapString(content.allOfCountry, { country: countryName });
  const confirmLabel = confirm
    ? confirm.areaName
      ? fillRegionMapString(content.useCityInArea, { city: confirm.city, area: confirm.areaName })
      : fillRegionMapString(content.useCity, { city: confirm.city })
    : null;

  const dense = variant === 'sheet';

  return (
    <div className={`flex h-full min-h-0 flex-col ${className}`}>
      {/* Head — not scrollable, so the escape hatch is always one tap away. */}
      <div className={dense ? 'px-4 pt-3' : 'px-5 pb-1 pt-5 max-md:pt-14'}>
        {variant === 'panel' && (
          <button
            type="button"
            onClick={onBack}
            className="-ml-1.5 mb-2 inline-flex items-center gap-1.5 rounded-lg px-1.5 py-1 text-xs font-semibold text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100"
          >
            <FiArrowLeft className="h-3.5 w-3.5" />
            {content.back}
          </button>
        )}
        <h2
          className={`font-bold leading-tight text-gray-900 dark:text-gray-100 ${
            dense ? 'text-base' : 'text-lg'
          }`}
        >
          {fillRegionMapString(content.chooseAreaTitle, { country: countryName })}
        </h2>
        {!dense && (
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{content.chooseAreaSubtitle}</p>
        )}
      </div>

      <div className={`flex-none space-y-2 ${dense ? 'px-4 pt-2.5' : 'px-4 pt-3'}`}>
        {confirm && confirmLabel && (
          <button
            type="button"
            onClick={onConfirm}
            className="flex w-full items-center gap-3 rounded-2xl bg-[#00a798] px-4 py-3 text-left shadow-md shadow-[#00a798]/25 transition-transform active:scale-[0.99]"
          >
            <FiMapPin className="h-5 w-5 flex-shrink-0 text-white" />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-bold text-white">{confirmLabel}</span>
              <span className="block text-[11px] text-white/80">{content.weFoundYou}</span>
            </span>
          </button>
        )}

        {/* Always first among the choices: this is the one click that keeps the
            new flow from being slower than the old redirect.

            It carries FULL primary weight (solid fill) whenever nothing else
            competes. As a tinted outline it read as just another list row, and
            people who did not already know the option existed never found it.
            When a resolved place owns the primary slot this drops to a 2px teal
            outline — still unmistakably a button, not a row. */}
        <button
          type="button"
          onClick={onSelectWholeCountry}
          className={`flex w-full items-center gap-3 rounded-2xl px-4 py-3.5 text-left transition-all active:scale-[0.99] ${
            confirm
              ? 'border-2 border-[#00a798] bg-white hover:bg-[#00a798]/10 dark:bg-gray-900/60 dark:hover:bg-[#00a798]/15'
              : 'border-2 border-transparent bg-[#00a798] shadow-lg shadow-[#00a798]/30 hover:bg-[#00937f]'
          }`}
        >
          {/* The country's own flag, not a generic globe: it names the choice
              at a glance and matches the flag on the country tile that got the
              visitor here. */}
          <span
            aria-hidden
            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-white/20 text-xl leading-none shadow-sm ring-1 ring-white/30 dark:ring-white/20"
          >
            {countryFlagEmoji(countryCode)}
          </span>
          <span className="min-w-0 flex-1">
            <span
              className={`block truncate text-base font-bold ${
                confirm ? 'text-gray-900 dark:text-gray-100' : 'text-white'
              }`}
            >
              {allOfCountryLabel}
            </span>
            <span
              className={`block text-[11px] ${
                confirm ? 'text-gray-500 dark:text-gray-400' : 'text-white/80'
              }`}
            >
              {content.allOfCountryHint}
            </span>
          </span>
          <FiChevronRight
            aria-hidden
            className={`h-4 w-4 flex-shrink-0 ${confirm ? 'text-[#00a798]' : 'text-white/90'}`}
          />
        </button>

        {/* The fork, said out loud. Everything below is the OTHER branch of the
            choice above, and without this line the list just looks like more of
            the same card. */}
        <div className="flex items-center gap-2 pt-1" aria-hidden>
          <span className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
          <span className="text-[11px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
            {content.orChooseArea}
          </span>
          <span className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
        </div>

        {showFilter && (
          <div className="relative pt-0.5">
            <FiSearch className="pointer-events-none absolute left-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              inputMode="search"
              autoComplete="off"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={content.filterPlaceholder}
              aria-label={content.filterPlaceholder}
              className="w-full rounded-full border-0 bg-gray-100 py-2 pl-9 pr-3 text-sm text-gray-900 placeholder-gray-500 shadow-sm transition-all focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#00a798]/30 dark:bg-gray-800/80 dark:text-gray-100 dark:placeholder-gray-400 dark:focus:bg-gray-800"
            />
          </div>
        )}
      </div>

      <ul
        ref={listRef}
        aria-label={fillRegionMapString(content.listAriaLabel, { country: countryName })}
        onPointerEnter={() => {
          pointerInsideRef.current = true;
        }}
        onPointerLeave={() => {
          pointerInsideRef.current = false;
          setHover(null);
        }}
        className="mt-2 min-h-0 flex-1 space-y-1 overflow-y-auto px-4 pb-4"
      >
        {visible.length === 0 && (
          <li className="px-2 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
            {content.noMatches}
          </li>
        )}
        {visible.map((unit) => {
          const isSelected = selectedCode === unit.code;
          const isHovered = hoveredCode === unit.code;
          return (
            <li key={unit.code}>
              <button
                type="button"
                data-subregion={unit.code}
                aria-pressed={isSelected}
                onMouseEnter={() => setHover(unit.code)}
                onFocus={() => setHover(unit.code)}
                onBlur={() => setHover(null)}
                onClick={() => onSelectSubregion(unit.code, unit.name)}
                className={`flex w-full items-center gap-2.5 rounded-xl border px-3 py-2 text-left transition-colors ${
                  isSelected
                    ? 'border-[#00a798] bg-[#00a798] text-white shadow-sm shadow-[#00a798]/20'
                    : isHovered
                      ? 'border-[#00a798]/50 bg-[#00a798]/10 dark:border-[#00a798]/50 dark:bg-[#00a798]/15'
                      : 'border-transparent hover:bg-gray-100/80 dark:hover:bg-gray-800/60'
                }`}
              >
                <span
                  aria-hidden
                  className={`h-2 w-2 flex-shrink-0 rounded-full ${
                    isSelected ? 'bg-white' : isHovered ? 'bg-[#00a798]' : 'bg-[#8dd4cd]'
                  }`}
                />
                <span
                  className={`min-w-0 flex-1 truncate text-sm font-medium ${
                    isSelected ? 'text-white' : 'text-gray-800 dark:text-gray-200'
                  }`}
                >
                  {unit.name}
                </span>
                {isSelected && <FiCheck className="h-4 w-4 flex-shrink-0 text-white" />}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default SubdivisionPanel;
